// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { cascadeApi } from "api/TenantSlug/Cascade/api";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import StoreCascadeRequest = App.Http.Requests.Cascade.StoreCascadeRequest;
import UpdateCascadeRequest = App.Http.Requests.Cascade.UpdateCascadeRequest;
import GetCascadesRequest = App.Http.Requests.Cascade.GetCascadesRequest;
import GetCascadeResponse = App.Http.Responses.Cascade.GetCascadeResponse;
import GetCascadesResponse = App.Http.Responses.Cascade.GetCascadesResponse;
import BatchInsertUpdateCascadeResponse = App.Http.Responses.Cascade.BatchInsertUpdateCascadeResponse;
import BatchUpdateCascadeRequest = App.Http.Requests.Cascade.BatchUpdateCascadeRequest;
import BatchStoreCascadeRequest = App.Http.Requests.Cascade.BatchStoreCascadeRequest;
import GetCascadeRequest = App.Http.Requests.Cascade.GetCascadeRequest;
import BaseBatchDeleteRequest = App.Http.Requests.BaseBatchDeleteRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

// Query to get a list of cascades
export const useGetCascadesQuery = (request: GetCascadesRequest, options = {}) => {
    return useQueryApi<GetCascadesResponse>({
        queryKey: ["getCascades", request],
        queryFn: () => cascadeApi.index(request),
        ...options,
    });
};

export const useGetCascadeQuery = (cascadeId: string, request: GetCascadeRequest, options = {}) => {
    return useQueryApi({
        queryKey: ["getCascade", cascadeId],
        queryFn: () => cascadeApi.show(cascadeId, request),
        ...options,
    });
};

// Mutation to store a new cascade
export const useStoreCascadeMutation = (
    options: UseMutationOptions<GetCascadeResponse, AxiosError<ApiErrorResponse>, StoreCascadeRequest> = {}
) => {
    return useMutationApi<GetCascadeResponse, StoreCascadeRequest>({
        mutationKey: ["storeCascade"],
        mutationFn: (request: StoreCascadeRequest) => cascadeApi.store(request),
        ...options,
    });
};

// Mutation to update an existing cascade
export const useUpdateCascadeMutation = (options = {}) => {
    return useMutationApi<GetCascadeResponse, { request: UpdateCascadeRequest; cascadeId: string }>({
        mutationKey: ["updateCascade"],
        mutationFn: ({ request, cascadeId }) => cascadeApi.update(request, cascadeId),
        ...options,
    });
};

// Mutation to batch store cascades
export const useBatchStoreCascadeMutation = (
    options: UseMutationOptions<
        BatchInsertUpdateCascadeResponse,
        AxiosError<ApiErrorResponse>,
        BatchStoreCascadeRequest
    > = {}
) => {
    return useMutationApi<BatchInsertUpdateCascadeResponse, BatchStoreCascadeRequest>({
        mutationKey: ["batchStoreCascade"],
        mutationFn: (request: BatchStoreCascadeRequest) => cascadeApi.batchStore(request),
        ...options,
    });
};

// Mutation to update an existing cascades with batch update
export const useBatchUpdateCascadeMutation = (
    options: UseMutationOptions<
        BatchInsertUpdateCascadeResponse,
        AxiosError<ApiErrorResponse>,
        BatchUpdateCascadeRequest
    > = {}
) => {
    return useMutationApi<BatchInsertUpdateCascadeResponse, BatchUpdateCascadeRequest>({
        mutationKey: ["batchUpdateCascade"],
        mutationFn: (request: BatchUpdateCascadeRequest) => cascadeApi.batchUpdate(request),
        ...options,
    });
};

// Mutation to destroy a cascade
export const useDestroyCascadeMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<null, string>({
        mutationKey: ["destroyCascade"],
        mutationFn: (cascadeId: string) => cascadeApi.destroy(cascadeId),
        ...options,
    });
};

// Mutation to batch delete cascades
export const useBatchDeleteCascadeMutation = (
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, BaseBatchDeleteRequest> = {}
) => {
    return useMutationApi<void, BaseBatchDeleteRequest>({
        mutationKey: ["batchDeleteCascade"],
        mutationFn: (request) => cascadeApi.batchDelete(request),
        ...options,
    });
};

// Mutation to export cascades
export const useExportCascadesMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportCascades"],
        mutationFn: (request) => cascadeApi.export(request),
        ...options,
    });
};
